import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useRoute } from '@react-navigation/native';
import { useMutation } from '@apollo/client';
import { View } from 'react-native';
import gql from 'graphql-tag';
import Toast from 'react-native-root-toast';

import { ROLE_PERMISSIONS, STATUS } from '../../../common/constants';
import { useUserContext } from '../../../common/utils';

import DotMenu from '../../components/navigation/DotMenu';
import { WarningDialog } from '../../components';
import { Screen } from '../../design/components';
import { useFreshDetailQueryOptions } from '../../hooks';
import useBackLabel from '../../hooks/useBackLabel';
import useFramedDetailStates from '../../hooks/useFramedDetailStates';
import { useThemedStyles } from '../../hooks/useThemedStyles';

import ServiceNoteButton from '../driver/components/ServiceNoteButton';
import PickListDetail from './PickListDetail';
import { RECALCULATE_MACHINE_PICKLIST } from '../../../common/graphql/pickList';
import { getLocalTime } from '../../../common/utils/time/time';
import { MODIFY_ALL_PICKLIST_ITEMS_QUANTITY_TO_ZERO } from '../../../common/graphql/pickListItem';
import { useOfflineQueue } from '../../offline';
import TrainingModeBanner from '../myDay/TrainingModeBanner';
import { isMyDayTraining } from '../myDay/myDayTraining';

const PickListDetailView = ({
	...otherProps
}) => {

	const { hasPermission, tenantSettings, tenant } = useUserContext();
	const { showPickListRecalculationDialog } = tenantSettings;
	const route = useRoute();
	const styles = useThemedStyles(createStyles);
	const { isOnline } = useOfflineQueue();
	const trainingContext = route.params?.myDayTraining;
	const isTraining = isMyDayTraining(trainingContext);
	const trainingOffline = isTraining && !isOnline;
	const isOnlineRef = useRef(isOnline);
	isOnlineRef.current = isOnline;
	const { planned, machineName, id, status, isAnyItemPicked, hasProductSoldSinceLastPickListRecalculation, startLabelPrinted, allowPartialCompletion } = route.params;
	// readOnly: opened from a stop without a pick step, to see what was packed without editing it.
	const isReadOnly = Boolean(route.params?.readOnly);
	const {
		canRenderDetailQuery,
		detailQueryOptions,
		blockDataWhileLoading,
	} = useFreshDetailQueryOptions({ noCache: isTraining });

	const isServiceRunInPast = getLocalTime(tenant.timezone).isAfter(getLocalTime(tenant.timezone, planned), 'day');

	const [modifyAllPicklistItemsQuantityToZeroMutation, { loading: loadingSetQuantitiesToZero }] = useMutation(MODIFY_ALL_PICKLIST_ITEMS_QUANTITY_TO_ZERO);

	const [isRecalculatePickListDialogOpen, setIsRecalculatePickListDialogOpen] = useState(!isReadOnly && hasProductSoldSinceLastPickListRecalculation && showPickListRecalculationDialog && !isAnyItemPicked && !isServiceRunInPast && status !== STATUS.COMPLETE);
	const [recalculateMachinePicklist, { loading: loadingMutation }] = useMutation(RECALCULATE_MACHINE_PICKLIST, {
		variables: { pickListId: id },
		// refetchQueries: [{ query, variables: { id } }],
		// awaitRefetchQueries: true,
	});

	const requireTrainingOnline = useCallback(() => {
		if (isTraining && !isOnlineRef.current) {
			Toast.show('Reconnect to continue training');
			return false;
		}
		return true;
	}, [isTraining]);

	const setAllQuantitiesToZero = useCallback(() => {
		if (!requireTrainingOnline()) {
			return undefined;
		}
		return modifyAllPicklistItemsQuantityToZeroMutation({ variables: { id } });
	}, [id, modifyAllPicklistItemsQuantityToZeroMutation, requireTrainingOnline]);

	const setAllUnpickedQuantitiesToZero = useCallback(() => {
		if (!requireTrainingOnline()) {
			return undefined;
		}
		return modifyAllPicklistItemsQuantityToZeroMutation({ variables: { id, unpicked: true } });
	}, [id, modifyAllPicklistItemsQuantityToZeroMutation, requireTrainingOnline]);

	const recalculatePickList = useCallback(() => {
		if (!requireTrainingOnline()) {
			return undefined;
		}
		return recalculateMachinePicklist();
	}, [recalculateMachinePicklist, requireTrainingOnline]);


	const canEditPickLists = hasPermission(ROLE_PERMISSIONS.ADD_EDIT_PICK_LISTS);
	const backLabel = useBackLabel();
	const banner = useMemo(() => (
		<TrainingModeBanner trainingContext={trainingContext} isOnline={isOnline} />
	), [isOnline, trainingContext]);
	const framedStates = useFramedDetailStates({ headerRight: <ServiceNoteButton />, backLabel, banner });
	const headerMenu = canEditPickLists && !isReadOnly ? (
		<MenuComponent
			setAllQuantitiesToZero={setAllQuantitiesToZero}
			setAllUnpickedQuantitiesToZero={setAllUnpickedQuantitiesToZero}
			setIsRecalculatePickListDialogOpen={setIsRecalculatePickListDialogOpen}
		/>
	) : null;

	return (
		<View style={styles.container}>
			{canRenderDetailQuery ? (
				<PickListDetail
					id={id}
					machineName={machineName}
					query={query}
					recalculateMachinePicklist={recalculatePickList}
					loadingRecalculation={loadingMutation}
					loadingSetQuantitiesToZero={loadingSetQuantitiesToZero}
					startLabelPrinted={startLabelPrinted}
					allowPartialCompletion={allowPartialCompletion}
					detailQueryOptions={detailQueryOptions}
					blockDataWhileLoading={blockDataWhileLoading}
					backLabel={backLabel}
					banner={banner}
					headerMenu={headerMenu}
					{...framedStates}
					{...otherProps}
					disableActions={trainingOffline || isReadOnly}
					readOnly={isReadOnly}
					trainingMode={isTraining}
				/>
			) : (
				<Screen headerRight={<ServiceNoteButton />} backLabel={backLabel} banner={banner} isLoading={true} />
			)}
			<WarningDialog
				title={hasProductSoldSinceLastPickListRecalculation && 'Sales made since last pick list recalculation' || 'Recalculate pick list'}
				texts={hasProductSoldSinceLastPickListRecalculation && ['Sales have been made since the last pick list recalculation! When this is the case, generally you should recalculate the pick list prior to starting picking to get the most up-to-date pick list.',
					'Would you like to recalculate the pick list now?'] || ['Would you like to recalculate the pick list now?']}
				setIsOpen={setIsRecalculatePickListDialogOpen}
				isOpen={isRecalculatePickListDialogOpen}
				onClick={recalculatePickList}
				isButtonDisabled={trainingOffline}
			/>
		</View>
	);
};

// Pick list tools for users who can edit pick lists (header dots menu).
const MenuComponent = ({
	setAllQuantitiesToZero,
	setAllUnpickedQuantitiesToZero,
	setIsRecalculatePickListDialogOpen,
}) => {
	const [isWarningDialogOpen, setIsWarningDialogOpen] = useState(false);
	const [isWarningUnpickedDialogOpen, setIsWarningUnpickedDialogOpen] = useState(false);
	const menuOptions = [
		{
			title: 'Set all quantities to 0',
			onPress: () => {
				return setIsWarningDialogOpen(true);
			},
		},
		{
			title: 'Set all unpicked quantities to 0',
			onPress: () => {
				return setIsWarningUnpickedDialogOpen(true);
			},
		},
		{
			title: 'Recalculate pick list',
			onPress: () => {
				return setIsRecalculatePickListDialogOpen(true);
			},
		},
	];

	return (
		<View>
			<DotMenu
				menuOptions={menuOptions}
				forceDots={true}
			/>
			<WarningDialog
				isOpen={isWarningDialogOpen}
				setIsOpen={setIsWarningDialogOpen}
				onClick={setAllQuantitiesToZero}
				texts={[
					'Using this function will reset ALL pick list items to have a quantity of zero. This includes any items that already have their checkbox marked as picked.',
					'This function may be useful to use in cases where you are only picking a few items and want to first reset the whole pick list to zero. Otherwise, it is not generally recommended to be used.',
					'Are you sure you want to reset the pick list to have all items set to zero?',
				]}
			/>
			<WarningDialog
				isOpen={isWarningUnpickedDialogOpen}
				setIsOpen={setIsWarningUnpickedDialogOpen}
				onClick={setAllUnpickedQuantitiesToZero}
				texts={[
					'This function will reset all UNPICKED items to have a quantity of zero. That is, it will set all picklist items that do not have their checkbox checked to have a quantity of zero.',
					'This function may be useful in cases where you have a number of items that are not available to be picked and where it is more convenient to first mark all available items as picked (using the checkboxes) and then to subsequently reset all unpicked items to zero.',
					'Are you sure you want to reset the pick list to have all unpicked items set to zero?',
				]}
			/>
		</View>
	);
};

const createStyles = ({ ui: { colors } }) => ({
	container: {
		backgroundColor: colors.canvas,
		flex: 1,
	},
});

export default PickListDetailView;

const query = gql`
	query($id: String) {
		getPickList (id: $id) {
			id
			calculatedAt
			pickers {
				id
				firstname
				lastname
				username
				destroyed
			}

			serviceRun {
				id
				planned
				route {
					id
					name
				}
				driver {
					id
					firstname
					lastname
					username
				}
			}
			serviceDocket {
				id
				cashBag
				tub
				isRestocked
				inventoryPartiallyPicked
				picker {
					id
					firstname
					lastname
					username
				}
				packTags {
					id
					name
					labelPrintOnly
				}
			}
			machine {
				id
				type
				averageDaysSinceLastService
				firstService
				ingredientPlanogram {
					id
				}
				planogram {
					id
					planogramItemGroups {
						id
						name
						type
						order
						layoutSettings
					}
				}
			}
			pickListItems {
				id
				isPicked
				extraQuantityIsPicked
				pickOrder
				planogramItem {
					id
					selection
					inactive
					quantity
					parlevel
					capacity
					price
					reportedPrice
					enabled
					isIngredient
					row
					column
					queuedPrice
					queuedPriceType
					planogramItemGroupId
					planogramItemStatistics {
						id
						salesLast7Days
						salesLast14Days
						salesLast30Days
						salesLast365Days
						lastProductChangeTime	
					}
					created
					expiryDates
				}
				selection

				quantity
				extraQuantity
				tubWeightExcludedQuantity
				unadjustedQuantity

				planogramItemQuantity
				planogramItemParlevel
				planogramItemQuantityPostService
				planogramItemQuantityPostInventoryChange
				planogramItemExpiryDatesAtPick
				suggestedExpiryDatesFromWarehouse

				originalPickQuantity
				originalPlanogramItemQuantity
				originalPickExtraQuantity
				lastCalculatedExtraQuantity

				product {
					id
					name
					audioName
					imageUrl
					HCGCategory
					HFDCategory
					weight
					excludeFromTubWeightCalculation
					measurementUnit
				}
				removedProduct {
					id
					name
					imageUrl
				}
			}
		}
	}
`;
